import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, Polyline } from '@react-google-maps/api';
import {
  MapPin,
  Maximize2,
  Minimize2,
  Navigation,
  Calendar,
  Clock,
  Route as RouteIcon,
  RotateCw,
  Info,
  List,
} from 'lucide-react';
import { googleMapsLoaderConfig } from '../../config/googleMapsConfig';
import { uberMapStyle } from '../../styles/mapStyle';
import { computeRoute } from '../../utils/routesApi';
import ambulanceIcon from '../../svg/Rectangle.png';
import { makePinSvg } from '../../utils/mapMarkers';
import { msToKmh, formatSpeedKmh } from '../../utils/speed';

// Clean timestamp pill (white on blue for vehicle, dark text on white for checkpoints)
const makeTimestampPillSvg = (timeText, isAmbulance = false) => {
  const bg = isAmbulance ? '#1A73E8' : '#FFFFFF';
  const stroke = isAmbulance ? '#1557B0' : '#2563EB';
  const textFill = isAmbulance ? '#FFFFFF' : '#1F2937';
  const width = Math.max(68, timeText.length * 8 + 16);
  const height = 24;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
    <rect x="1" y="1" width="${width - 2}" height="${height - 2}" rx="5" fill="${bg}" stroke="${stroke}" stroke-width="1.5"/>
    <text x="${width / 2}" y="15.5" font-family="-apple-system, BlinkMacSystemFont, Arial, sans-serif" font-size="11" font-weight="700" fill="${textFill}" text-anchor="middle">${timeText}</text>
  </svg>`;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    width,
    height,
  };
};

const RoutePlaybackMap = ({
  routeData = [],
  pickupLocation = null,
  dropoffLocation = null,
  pickupAddress: propPickupAddress = null,
  dropoffAddress: propDropoffAddress = null,
  trackingEvents = [],
  bookingStatus = null,
  zoom = 14,
  height = '100%',
  showControls = true,
  hidePickupDropoff = false,
  sampleIntervalSeconds = 30, // 30-second default for clean display
  onRefresh = null,
  startTime = null,
  endTime = null,
  durationMinutes = null,
  tripStartTime = null,
  tripEndTime = null,
  tripDurationMinutes = null,
}) => {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const boundsFittedRef = useRef(false);
  const userInteractedRef = useRef(false);
  const isProgrammaticMoveRef = useRef(false);
  const markersRef = useRef([]); // Imperative Google Maps markers store

  // Resolve Pickup Address (from prop, or from pickupLocation object)
  const resolvedPickupAddress = useMemo(() => {
    if (propPickupAddress) return propPickupAddress;
    if (typeof pickupLocation === 'string') return pickupLocation;
    return (
      pickupLocation?.address ||
      pickupLocation?.formatted_address ||
      pickupLocation?.name ||
      pickupLocation?.locationName ||
      pickupLocation?.pickup_address ||
      null
    );
  }, [propPickupAddress, pickupLocation]);

  // Resolve Dropoff / Destination Address (from prop, or from dropoffLocation object)
  const resolvedDropoffAddress = useMemo(() => {
    if (propDropoffAddress) return propDropoffAddress;
    if (typeof dropoffLocation === 'string') return dropoffLocation;
    return (
      dropoffLocation?.address ||
      dropoffLocation?.formatted_address ||
      dropoffLocation?.name ||
      dropoffLocation?.hospital_name ||
      dropoffLocation?.hospitalName ||
      dropoffLocation?.locationName ||
      dropoffLocation?.dropoff_address ||
      null
    );
  }, [propDropoffAddress, dropoffLocation]);

  const [map, setMap] = useState(null);
  const [displayCenter, setDisplayCenter] = useState({ lat: 23.1911, lng: 72.6162 });
  const [displayZoom, setDisplayZoom] = useState(zoom || 14);
  const [directionRoutePath, setDirectionRoutePath] = useState([]);
  const [actualDrivenRoutePath, setActualDrivenRoutePath] = useState([]);
  const driverPolylineRef = useRef(null);
  const driverGlowRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [activeTab, setActiveTab] = useState('timeline'); // 'timeline' | 'locations'
  const [viewMode, setViewMode] = useState('summary'); // 'summary' (30s default) | 'detailed' (5s raw)
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Debug logging - Only log when there's actual route data or errors
  if (routeData && routeData.length > 0) {
    console.log('[RoutePlaybackMap] Rendering with route', {
      totalPoints: routeData.length,
      hasPickup: !!pickupLocation,
      hasDropoff: !!dropoffLocation,
    });
  }
  const { isLoaded } = useJsApiLoader({
    ...googleMapsLoaderConfig,
  });

  // Start & End positions derived from pickup/dropoff or points
  const startPosition = useMemo(() => {
    if (pickupLocation) {
      const lat = pickupLocation.latitude || pickupLocation.lat;
      const lng = pickupLocation.longitude || pickupLocation.lng || pickupLocation.lon;
      if (lat != null && lng != null) {
        const latitude = typeof lat === 'string' ? parseFloat(lat) : lat;
        const longitude = typeof lng === 'string' ? parseFloat(lng) : lng;
        if (!isNaN(latitude) && !isNaN(longitude)) return { lat: latitude, lng: longitude };
      }
    }
    return null;
  }, [pickupLocation]);

  const endPosition = useMemo(() => {
    if (dropoffLocation) {
      const lat = dropoffLocation.latitude || dropoffLocation.lat;
      const lng = dropoffLocation.longitude || dropoffLocation.lng || dropoffLocation.lon;
      if (lat != null && lng != null) {
        const latitude = typeof lat === 'string' ? parseFloat(lat) : lat;
        const longitude = typeof lng === 'string' ? parseFloat(lng) : lng;
        if (!isNaN(latitude) && !isNaN(longitude)) return { lat: latitude, lng: longitude };
      }
    }
    return null;
  }, [dropoffLocation]);

  // Raw processed GPS points from backend (sorted chronologically)
  const rawRoutePath = useMemo(() => {
    const rawList = Array.isArray(routeData)
      ? routeData
      : Array.isArray(routeData?.data)
      ? routeData.data
      : [];

    let path = rawList
      .filter(point => point && (point.latitude != null || point.lat != null) && (point.longitude != null || point.lng != null))
      .map((point, index) => {
        const rawLat = point.latitude ?? point.lat;
        const rawLng = point.longitude ?? point.lng;
        return {
          lat: typeof rawLat === 'string' ? parseFloat(rawLat) : rawLat,
          lng: typeof rawLng === 'string' ? parseFloat(rawLng) : rawLng,
          timestamp: point.timestamp || point.created_at || point.recorded_at || point.time || point.timestamp_at,
          speed: point.speed != null ? parseFloat(point.speed) : null,
          heading: point.heading || point.bearing || null,
          index,
        };
      })
      .filter(point => !isNaN(point.lat) && !isNaN(point.lng));

    if (path.length > 0 && path.some(p => p.timestamp)) {
      path = path.sort((a, b) => {
        if (!a.timestamp && !b.timestamp) return a.index - b.index;
        if (!a.timestamp) return 1;
        if (!b.timestamp) return -1;
        try {
          const timeA = new Date(a.timestamp).getTime();
          const timeB = new Date(b.timestamp).getTime();
          if (isNaN(timeA) || isNaN(timeB)) return a.index - b.index;
          return timeA - timeB;
        } catch {
          return a.index - b.index;
        }
      });
    }

    return path;
  }, [routeData]);

  // Effective full raw path: if backend returned points use them; otherwise connect pickup & dropoff only if both exist
  const effectiveRawPath = useMemo(() => {
    if (rawRoutePath.length >= 2) return rawRoutePath;

    // Direct path between pickup and dropoff if both coordinates are provided
    if (startPosition && endPosition) {
      return [
        { lat: startPosition.lat, lng: startPosition.lng, timestamp: null, speed: null, index: 0 },
        { lat: endPosition.lat, lng: endPosition.lng, timestamp: null, speed: null, index: 1 },
      ];
    }

    return rawRoutePath;
  }, [rawRoutePath, startPosition, endPosition]);

  // 30s Summary Points (~8 to 10 clean checkpoint points)
  const summaryPoints = useMemo(() => {
    if (effectiveRawPath.length <= 10) {
      return effectiveRawPath;
    }

    // Try time-based downsampling with 25-30s difference
    const filtered = [effectiveRawPath[0]];
    let lastTime = effectiveRawPath[0].timestamp ? new Date(effectiveRawPath[0].timestamp).getTime() : NaN;

    for (let i = 1; i < effectiveRawPath.length - 1; i++) {
      const pt = effectiveRawPath[i];
      if (pt.timestamp) {
        const ptTime = new Date(pt.timestamp).getTime();
        if (!isNaN(ptTime) && !isNaN(lastTime)) {
          if (Math.abs((ptTime - lastTime) / 1000) >= (sampleIntervalSeconds || 30) - 5) {
            filtered.push(pt);
            lastTime = ptTime;
            continue;
          }
        }
      }
    }

    // Always include last destination point
    filtered.push(effectiveRawPath[effectiveRawPath.length - 1]);

    // Guarantee ~8 to 10 points even if timestamps are dense or missing
    if (filtered.length < 6) {
      const targetCount = Math.min(10, effectiveRawPath.length);
      const step = (effectiveRawPath.length - 1) / (targetCount - 1);
      const sampled = [];
      for (let i = 0; i < targetCount; i++) {
        const idx = Math.min(effectiveRawPath.length - 1, Math.round(i * step));
        sampled.push(effectiveRawPath[idx]);
      }
      return sampled;
    }

    return filtered;
  }, [effectiveRawPath, sampleIntervalSeconds]);

  // Active routePath for markers depending on viewMode:
  // viewMode === 'summary' -> 30s summary (~10 points)
  // viewMode === 'detailed' -> all 33 raw points (5s pings)
  const routePath = useMemo(() => {
    if (viewMode === 'detailed') {
      return effectiveRawPath;
    }
    return summaryPoints;
  }, [viewMode, effectiveRawPath, summaryPoints]);

  // Detect if the driver actually moved along roads (at least 50 meters of movement)
  const hasRealDriverMovement = useMemo(() => {
    if (!effectiveRawPath || effectiveRawPath.length < 2) return false;
    let dist = 0;
    for (let i = 1; i < effectiveRawPath.length; i++) {
      const p1 = effectiveRawPath[i - 1];
      const p2 = effectiveRawPath[i];
      const lat1 = parseFloat(p1.lat ?? p1.latitude);
      const lng1 = parseFloat(p1.lng ?? p1.longitude);
      const lat2 = parseFloat(p2.lat ?? p2.latitude);
      const lng2 = parseFloat(p2.lng ?? p2.longitude);
      if ([lat1, lng1, lat2, lng2].some(v => isNaN(v))) continue;
      const dLat = (lat2 - lat1) * 111.32;
      const dLng = (lng2 - lng1) * 111.32 * Math.cos((lat1 * Math.PI) / 180);
      dist += Math.sqrt(dLat * dLat + dLng * dLng);
    }
    return dist >= 0.05;
  }, [effectiveRawPath]);

  const effectiveStartTimeProp = startTime || tripStartTime;
  const effectiveEndTimeProp = endTime || tripEndTime;
  const effectiveDurationProp = durationMinutes ?? tripDurationMinutes;

  // Route statistics (duration, distance, startTime, endTime)
  const routeStats = useMemo(() => {
    const list = effectiveRawPath || [];
    const firstPt = list.length > 0 ? list[0] : null;
    const lastPt = list.length > 0 ? list[list.length - 1] : null;

    let startTimeStr = '—';
    let endTimeStr = '—';
    let durationStr = '—';

    // 1. Time range formatting: prefer booking start/end if provided, falling back to GPS points
    const effectiveStartTimestamp = effectiveStartTimeProp || firstPt?.timestamp;
    const effectiveEndTimestamp = effectiveEndTimeProp || lastPt?.timestamp;

    if (effectiveStartTimestamp) {
      try {
        const start = new Date(effectiveStartTimestamp);
        if (!isNaN(start.getTime())) {
          startTimeStr = start.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
        }
      } catch (err) {
        console.warn('Failed parsing start timestamp', err);
      }
    }

    if (effectiveEndTimestamp) {
      try {
        const end = new Date(effectiveEndTimestamp);
        if (!isNaN(end.getTime())) {
          endTimeStr = end.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
        }
      } catch (err) {
        console.warn('Failed parsing end timestamp', err);
      }
    }

    if (firstPt?.timestamp && lastPt?.timestamp) {
      try {
        const start = new Date(firstPt.timestamp);
        const end = new Date(lastPt.timestamp);
        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          const diffMs = Math.max(0, end - start);
          const diffMins = Math.floor(diffMs / 60000);
          const hours = Math.floor(diffMins / 60);
          const mins = diffMins % 60;
          durationStr = hours > 0 ? `${hours}h ${mins}m` : `${diffMins} min`;
        }
      } catch (err) {
        console.warn('Failed parsing timestamps for duration', err);
      }
    }

    // Great circle total distance in km
    // GPS history rows use latitude/longitude; lat/lng is accepted as a fallback
    let totalDistKm = 0;
    for (let i = 1; i < list.length; i++) {
      const lat1 = parseFloat(list[i - 1].latitude ?? list[i - 1].lat);
      const lng1 = parseFloat(list[i - 1].longitude ?? list[i - 1].lng);
      const lat2 = parseFloat(list[i].latitude ?? list[i].lat);
      const lng2 = parseFloat(list[i].longitude ?? list[i].lng);
      if ([lat1, lng1, lat2, lng2].some(v => v == null || isNaN(v))) continue;

      const R = 6371;
      const dLat = ((lat2 - lat1) * Math.PI) / 180;
      const dLng = ((lng2 - lng1) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      totalDistKm += R * c;
    }

    let displayDist = totalDistKm;
    if (displayDist < 0.05 && directionRoutePath && directionRoutePath.length > 1) {
      let dirDist = 0;
      for (let i = 1; i < directionRoutePath.length; i++) {
        const p1 = directionRoutePath[i - 1];
        const p2 = directionRoutePath[i];
        const dLat = (p2.lat - p1.lat) * 111.32;
        const dLng = (p2.lng - p1.lng) * 111.32 * Math.cos((p1.lat * Math.PI) / 180);
        dirDist += Math.sqrt(dLat * dLat + dLng * dLng);
      }
      if (dirDist > 0) displayDist = dirDist;
    }
    if (displayDist < 0.05 && startPosition && endPosition) {
      const dLat = (endPosition.lat - startPosition.lat) * 111.32;
      const dLng = (endPosition.lng - startPosition.lng) * 111.32 * Math.cos((startPosition.lat * Math.PI) / 180);
      const straightDist = Math.sqrt(dLat * dLat + dLng * dLng);
      if (straightDist > 0) displayDist = straightDist * 1.25;
    }

    // Prefer official booking duration if provided, matching table view
    let finalDuration = durationStr;
    if (effectiveDurationProp != null && !isNaN(Number(effectiveDurationProp)) && Number(effectiveDurationProp) > 0) {
      const m = Math.round(Number(effectiveDurationProp));
      if (m < 60) {
        finalDuration = `${m} mins`;
      } else {
        const hours = Math.floor(m / 60);
        const rem = m % 60;
        finalDuration = rem > 0 ? `${hours}h ${rem}m` : `${hours}h`;
      }
    } else if (finalDuration === '—' && effectiveStartTimestamp && effectiveEndTimestamp) {
      try {
        const start = new Date(effectiveStartTimestamp);
        const end = new Date(effectiveEndTimestamp);
        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          const diffMs = Math.max(0, end - start);
          const diffMins = Math.round(diffMs / 60000);
          if (diffMins > 0) {
            finalDuration = diffMins < 60 ? `${diffMins} mins` : `${Math.floor(diffMins / 60)}h ${diffMins % 60}m`;
          }
        }
      } catch (err) {
        console.warn('Failed calculating duration from start/end', err);
      }
    } else if ((finalDuration === '—' || finalDuration === '0 min') && displayDist > 0.05) {
      finalDuration = `${Math.max(2, Math.round((displayDist / 30) * 60))} min`;
    }

    return {
      startTime: startTimeStr,
      endTime: endTimeStr,
      duration: finalDuration,
      distance: displayDist > 0 ? displayDist.toFixed(2) : '0.00',
      pointCount: (routePath || []).length,
    };
  }, [effectiveRawPath, routePath, directionRoutePath, startPosition, endPosition, effectiveStartTimeProp, effectiveEndTimeProp, effectiveDurationProp]);

  // First Point & Center Point
  const firstPoint = useMemo(() => {
    if (effectiveRawPath.length > 0) return effectiveRawPath[0];
    if (startPosition) return startPosition;
    return null;
  }, [effectiveRawPath, startPosition]);

  const initialCenter = useMemo(() => {
    if (effectiveRawPath.length > 0) {
      const mid = effectiveRawPath[Math.floor(effectiveRawPath.length / 2)];
      return { lat: mid.lat, lng: mid.lng };
    }
    if (startPosition) return startPosition;
    return { lat: 23.0225, lng: 72.5714 }; // Default Ahmedabad center
  }, [effectiveRawPath, startPosition]);

  // Representative ambulance point (shown at ~35% of the active route path)
  const ambulanceIndex = useMemo(() => {
    if (routePath.length <= 1) return -1;
    if (routePath.length <= 3) return 1;
    return Math.min(routePath.length - 2, Math.max(1, Math.floor(routePath.length * 0.35)));
  }, [routePath]);

  const ambulancePoint = useMemo(() => {
    if (ambulanceIndex >= 0 && routePath[ambulanceIndex]) {
      return routePath[ambulanceIndex];
    }
    return null;
  }, [ambulanceIndex, routePath]);

  const ambulanceTime = useMemo(() => {
    if (!ambulancePoint?.timestamp) return 'Live';
    try {
      const d = new Date(ambulancePoint.timestamp);
      return isNaN(d.getTime()) ? 'Live' : d.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return 'Live';
    }
  }, [ambulancePoint]);

  // Timeline Milestones for Sidebar
  const timelineMilestones = useMemo(() => {
    const formatTime = (ts) => {
      if (!ts) return '—';
      try {
        const d = new Date(ts);
        return isNaN(d.getTime()) ? '—' : d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
      } catch {
        return '—';
      }
    };

    if (effectiveRawPath.length >= 2) {
      const p0 = effectiveRawPath[0];
      const pLast = effectiveRawPath[effectiveRawPath.length - 1];

      const items = [
        {
          id: 'tl-start',
          title: 'Started',
          time: formatTime(p0?.timestamp),
          lat: p0?.lat ?? startPosition?.lat,
          lng: p0?.lng ?? startPosition?.lng,
        },
      ];

      if (effectiveRawPath.length >= 4) {
        const p1 = effectiveRawPath[Math.min(1, effectiveRawPath.length - 2)];
        items.push({
          id: 'tl-pickup',
          title: 'At Pickup',
          time: formatTime(p1?.timestamp),
          lat: p1?.lat ?? startPosition?.lat,
          lng: p1?.lng ?? startPosition?.lng,
          address: resolvedPickupAddress, // Pickup location address
        });

        if (ambulancePoint) {
          items.push({
            id: 'tl-pickedup',
            title: 'Picked Up',
            time: formatTime(ambulancePoint.timestamp),
            lat: ambulancePoint.lat,
            lng: ambulancePoint.lng,
          });
        }

        const midIdx = Math.floor(effectiveRawPath.length * 0.65);
        if (midIdx > 1 && midIdx < effectiveRawPath.length - 1) {
          const pMid = effectiveRawPath[midIdx];
          items.push({
            id: 'tl-enroute-1',
            title: 'En Route',
            time: formatTime(pMid.timestamp),
            lat: pMid.lat,
            lng: pMid.lng,
          });
        }

        const lateIdx = Math.floor(effectiveRawPath.length * 0.85);
        if (lateIdx > midIdx && lateIdx < effectiveRawPath.length - 1) {
          const pLate = effectiveRawPath[lateIdx];
          items.push({
            id: 'tl-enroute-2',
            title: 'En Route',
            time: formatTime(pLate.timestamp),
            lat: pLate.lat,
            lng: pLate.lng,
          });
        }
      }

      items.push({
        id: 'tl-dropoff',
        title: 'Arrived (Dropoff)',
        time: formatTime(pLast?.timestamp),
        lat: pLast?.lat ?? endPosition?.lat,
        lng: pLast?.lng ?? endPosition?.lng,
        address: resolvedDropoffAddress, // Destination / dropoff address
      });

      return items;
    }

    const fallbackItems = [];
    if (startPosition || resolvedPickupAddress) {
      fallbackItems.push({
        id: 'tl-pickup-fallback',
        title: 'Pickup Location',
        time: '—',
        lat: startPosition?.lat,
        lng: startPosition?.lng,
        address: resolvedPickupAddress,
      });
    }
    if (endPosition || resolvedDropoffAddress) {
      fallbackItems.push({
        id: 'tl-dropoff-fallback',
        title: 'Destination',
        time: '—',
        lat: endPosition?.lat,
        lng: endPosition?.lng,
        address: resolvedDropoffAddress,
      });
    }
    return fallbackItems;
  }, [effectiveRawPath, ambulancePoint, startPosition, endPosition, resolvedPickupAddress, resolvedDropoffAddress]);

  // Imperative Google Maps Marker Management:
  // Render Pickup, Dropoff, Ambulance, Checkpoint Dots, and Timestamp Pills directly via window.google.maps.Marker
  useEffect(() => {
    if (!map || !mapReady || !window.google?.maps) return;

    // Clear previous markers
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    // 1. Pickup Marker (Google Blue Pin)
    const effectiveStart = startPosition || (effectiveRawPath.length > 0 ? effectiveRawPath[0] : null);
    if (!hidePickupDropoff && effectiveStart) {
      const pickupM = new window.google.maps.Marker({
        map,
        position: { lat: effectiveStart.lat, lng: effectiveStart.lng },
        title: 'Pickup Point',
        icon: {
          url: makePinSvg('blue'),
          scaledSize: new window.google.maps.Size(26, 38),
          anchor: new window.google.maps.Point(13, 38),
        },
        zIndex: 1010,
        optimized: false,
      });
      markersRef.current.push(pickupM);
    }

    // 2. Dropoff Marker (Google Red Pin)
    const effectiveEnd = endPosition || (effectiveRawPath.length > 0 ? effectiveRawPath[effectiveRawPath.length - 1] : null);
    if (!hidePickupDropoff && effectiveEnd) {
      const dropoffM = new window.google.maps.Marker({
        map,
        position: { lat: effectiveEnd.lat, lng: effectiveEnd.lng },
        title: 'Dropoff Point',
        icon: {
          url: makePinSvg('red'),
          scaledSize: new window.google.maps.Size(26, 38),
          anchor: new window.google.maps.Point(13, 38),
        },
        zIndex: 1010,
        optimized: false,
      });
      markersRef.current.push(dropoffM);
    }

    // 3. Ambulance Marker & Blue Timestamp Pill Badge (Commented out as requested)
    /*
    if (ambulancePoint) {
      const ambM = new window.google.maps.Marker({
        map,
        position: { lat: ambulancePoint.lat, lng: ambulancePoint.lng },
        icon: {
          url: ambulanceIcon,
          scaledSize: new window.google.maps.Size(46, 46),
          anchor: new window.google.maps.Point(23, 23),
        },
        zIndex: 1008,
        optimized: false,
        title: `Ambulance at ${ambulanceTime}`,
      });
      markersRef.current.push(ambM);

      const ambPill = makeTimestampPillSvg(ambulanceTime, true);
      const ambLabel = new window.google.maps.Marker({
        map,
        position: { lat: ambulancePoint.lat, lng: ambulancePoint.lng },
        icon: {
          url: ambPill.url,
          scaledSize: new window.google.maps.Size(ambPill.width, ambPill.height),
          anchor: new window.google.maps.Point(ambPill.width / 2, ambPill.height + 22),
        },
        zIndex: 1009,
        optimized: false,
      });
      markersRef.current.push(ambLabel);
    }
    */

    // Helper to calculate distance in meters between two lat/lng coordinates
    const getDistMeters = (p1, p2) => {
      if (!p1 || !p2 || p1.lat == null || p2.lat == null) return 999999;
      const R = 6371000;
      const dLat = ((p2.lat - p1.lat) * Math.PI) / 180;
      const dLng = ((p2.lng - p1.lng) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((p1.lat * Math.PI) / 180) * Math.cos((p2.lat * Math.PI) / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    let lastPlacedPt = null;

    // 4. Intermediate Checkpoint Target Dots & White Timestamp Pills
    routePath.forEach((pt, idx) => {
      // Skip start and end points (ambulance vehicle is commented out)
      if (idx === 0 || idx === routePath.length - 1) {
        return;
      }

      // Avoid placing checkpoint on top of start/end pins
      if (effectiveStart && getDistMeters(pt, effectiveStart) < 45) return;
      if (effectiveEnd && getDistMeters(pt, effectiveEnd) < 45) return;

      // Avoid overlapping consecutive checkpoints
      const minDistance = viewMode === 'detailed' ? 30 : 60;
      if (lastPlacedPt && getDistMeters(pt, lastPlacedPt) < minDistance) {
        return;
      }
      lastPlacedPt = pt;

      // Outer target blue circle
      const dotMarker = new window.google.maps.Marker({
        map,
        position: { lat: pt.lat, lng: pt.lng },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: '#1A73E8',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
          scale: 6,
        },
        zIndex: 1002,
        optimized: false,
      });
      markersRef.current.push(dotMarker);

      // Inner white dot
      const centerDot = new window.google.maps.Marker({
        map,
        position: { lat: pt.lat, lng: pt.lng },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: '#FFFFFF',
          fillOpacity: 1,
          strokeColor: '#1A73E8',
          strokeWeight: 1,
          scale: 2.2,
        },
        zIndex: 1003,
        optimized: false,
      });
      markersRef.current.push(centerDot);

      // White timestamp pill
      const timeStr = pt.timestamp
        ? new Date(pt.timestamp).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          })
        : `#${idx + 1}`;

      const pill = makeTimestampPillSvg(timeStr, false);
      const pillMarker = new window.google.maps.Marker({
        map,
        position: { lat: pt.lat, lng: pt.lng },
        icon: {
          url: pill.url,
          scaledSize: new window.google.maps.Size(pill.width, pill.height),
          anchor: new window.google.maps.Point(pill.width / 2, pill.height + 8),
        },
        zIndex: 1004,
        optimized: false,
      });
      markersRef.current.push(pillMarker);
    });

    return () => {
      markersRef.current.forEach(m => m.setMap(null));
      markersRef.current = [];
    };
  }, [map, mapReady, routePath, ambulancePoint, ambulanceIndex, ambulanceTime, startPosition, endPosition, effectiveRawPath, hidePickupDropoff, viewMode]);

  // Fit bounds to cover all points (pickup, dropoff, raw points, and road route)
  const fitMapBounds = useCallback(
    (force = false) => {
      if (!map || !window.google?.maps) return;
      if (userInteractedRef.current && !force) return;

      const bounds = new window.google.maps.LatLngBounds();
      let hasPoints = false;

      if (startPosition) {
        bounds.extend(new window.google.maps.LatLng(startPosition.lat, startPosition.lng));
        hasPoints = true;
      }
      if (endPosition) {
        bounds.extend(new window.google.maps.LatLng(endPosition.lat, endPosition.lng));
        hasPoints = true;
      }
      if (hasRealDriverMovement && effectiveRawPath.length > 0) {
        effectiveRawPath.forEach(pt => {
          if (pt && isFinite(pt.lat) && isFinite(pt.lng)) {
            bounds.extend(new window.google.maps.LatLng(pt.lat, pt.lng));
            hasPoints = true;
          }
        });
      }
      if (directionRoutePath.length > 0) {
        directionRoutePath.forEach(pt => {
          if (pt && isFinite(pt.lat) && isFinite(pt.lng)) {
            bounds.extend(new window.google.maps.LatLng(pt.lat, pt.lng));
            hasPoints = true;
          }
        });
      }

      isProgrammaticMoveRef.current = true;
      if (hasPoints && !bounds.isEmpty()) {
        map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
      } else {
        map.setCenter(initialCenter);
        map.setZoom(zoom || 14);
      }
      setTimeout(() => {
        isProgrammaticMoveRef.current = false;
      }, 500);
    },
    [map, effectiveRawPath, directionRoutePath, startPosition, endPosition, hasRealDriverMovement, initialCenter, zoom]
  );

  useEffect(() => {
    if (mapReady && map && !boundsFittedRef.current) {
      fitMapBounds(true);
      boundsFittedRef.current = true;
    }
  }, [mapReady, map, fitMapBounds]);

  useEffect(() => {
    if (mapReady && map && directionRoutePath.length > 0 && !userInteractedRef.current) {
      fitMapBounds(false);
    }
  }, [mapReady, map, directionRoutePath.length, fitMapBounds]);

  useEffect(() => {
    if (!map || !window.google?.maps) return undefined;
    const id = setTimeout(() => {
      window.google.maps.event.trigger(map, 'resize');
      if (!userInteractedRef.current) {
        fitMapBounds(false);
      }
    }, 100);
    return () => clearTimeout(id);
  }, [map, isFullscreen, fitMapBounds]);

  // Map load callback: listens to 'idle' only to avoid recursive center_changed updates
  const onLoad = useCallback(
    mapInstance => {
      mapRef.current = mapInstance;
      setMap(mapInstance);
      setMapReady(true);

      mapInstance.addListener('idle', () => {
        const c = mapInstance.getCenter();
        const z = mapInstance.getZoom();
        if (c) {
          setDisplayCenter({ lat: c.lat(), lng: c.lng() });
        }
        if (z != null) {
          setDisplayZoom(z);
        }
      });
    },
    []
  );

  const onUnmount = useCallback(() => {
    mapRef.current = null;
    setMap(null);
  }, []);

  // Always compute directions route between pickup and dropoff
  useEffect(() => {
    if (!startPosition || !endPosition) return undefined;
    let cancelled = false;

    computeRoute(startPosition, endPosition)
      .then(res => {
        if (cancelled) return;
        if (res?.coordinates?.length) {
          setDirectionRoutePath(res.coordinates);
        } else {
          setDirectionRoutePath([startPosition, endPosition]);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDirectionRoutePath([startPosition, endPosition]);
        }
      });

    return () => { cancelled = true; };
  }, [startPosition, endPosition]);

  // ── Actual Driven Route (Blue) ─────────────────────────────────────────────
  // Computes the actual route driven by the ambulance based on recorded GPS points
  useEffect(() => {
    if (!effectiveRawPath || effectiveRawPath.length < 2) {
      setActualDrivenRoutePath([]);
      return undefined;
    }

    const rawCoords = effectiveRawPath
      .map(p => ({
        lat: Number(p.lat ?? p.latitude),
        lng: Number(p.lng ?? p.longitude),
      }))
      .filter(p => isFinite(p.lat) && isFinite(p.lng));

    if (rawCoords.length < 2) {
      setActualDrivenRoutePath([]);
      return undefined;
    }

    // Immediately set raw points so the blue line appears on map without any delay
    setActualDrivenRoutePath(rawCoords);

    let cancelled = false;

    // Road-snap using DirectionsService with waypoints if available
    if (window.google?.maps?.DirectionsService) {
      const directionsService = new window.google.maps.DirectionsService();
      const origin = rawCoords[0];
      const destination = rawCoords[rawCoords.length - 1];

      // Sample up to 8 waypoints along the path
      const intermediate = rawCoords.slice(1, -1);
      const waypoints = [];
      const maxWaypoints = 8;
      if (intermediate.length <= maxWaypoints) {
        intermediate.forEach(p => waypoints.push({ location: new window.google.maps.LatLng(p.lat, p.lng), stopover: false }));
      } else {
        const step = (intermediate.length - 1) / (maxWaypoints - 1);
        for (let i = 0; i < maxWaypoints; i++) {
          const idx = Math.min(intermediate.length - 1, Math.round(i * step));
          waypoints.push({ location: new window.google.maps.LatLng(intermediate[idx].lat, intermediate[idx].lng), stopover: false });
        }
      }

      directionsService.route(
        {
          origin: new window.google.maps.LatLng(origin.lat, origin.lng),
          destination: new window.google.maps.LatLng(destination.lat, destination.lng),
          waypoints,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (cancelled) return;
          if (status === window.google.maps.DirectionsStatus.OK && result) {
            const overview = result.routes?.[0]?.overview_path;
            if (overview && overview.length > 0) {
              setActualDrivenRoutePath(overview.map(p => ({ lat: p.lat(), lng: p.lng() })));
            }
          }
        }
      );
    }

    return () => { cancelled = true; };
  }, [effectiveRawPath]);

  // Imperative drawing of the actual driven route directly on Google Maps canvas
  // Guaranteed rendering, eliminating any React reconciliation / timing bugs
  useEffect(() => {
    if (!map || !window.google?.maps || actualDrivenRoutePath.length < 2) {
      driverPolylineRef.current?.setMap(null);
      driverGlowRef.current?.setMap(null);
      driverPolylineRef.current = null;
      driverGlowRef.current = null;
      return;
    }

    const latLngPath = actualDrivenRoutePath.map(p => new window.google.maps.LatLng(p.lat, p.lng));

    if (!driverPolylineRef.current) {
      driverGlowRef.current = new window.google.maps.Polyline({
        map,
        path: latLngPath,
        strokeColor: '#1E40AF',
        strokeOpacity: 0.3,
        strokeWeight: 10,
        geodesic: true,
        zIndex: 996,
      });

      driverPolylineRef.current = new window.google.maps.Polyline({
        map,
        path: latLngPath,
        strokeColor: '#1A73E8',
        strokeOpacity: 1,
        strokeWeight: 5.5,
        geodesic: true,
        zIndex: 998,
        icons: [
          {
            icon: {
              path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
              fillColor: '#1A73E8',
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 1.5,
              scale: 3,
            },
            offset: '25px',
            repeat: '65px',
          },
        ],
      });
    } else {
      driverGlowRef.current.setPath(latLngPath);
      driverPolylineRef.current.setPath(latLngPath);
      driverGlowRef.current.setMap(map);
      driverPolylineRef.current.setMap(map);
    }

    return () => {
      driverPolylineRef.current?.setMap(null);
      driverGlowRef.current?.setMap(null);
      driverPolylineRef.current = null;
      driverGlowRef.current = null;
    };
  }, [map, actualDrivenRoutePath]);

  // Fullscreen toggler
  const toggleFullscreen = useCallback(() => {
    const el = mapContainerRef.current;
    if (!el) return;

    if (!document.fullscreenElement) {
      el.requestFullscreen?.()
        .then(() => setIsFullscreen(true))
        .catch(err => console.error('Fullscreen failed', err));
    } else {
      document.exitFullscreen?.()
        .then(() => setIsFullscreen(false))
        .catch(err => console.error('Exit fullscreen failed', err));
    }
  }, []);

  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  const handleRefresh = () => {
    setIsRefreshing(true);
    userInteractedRef.current = false;
    boundsFittedRef.current = false;
    fitMapBounds(true);
    if (onRefresh) onRefresh();
    setTimeout(() => setIsRefreshing(false), 600);
  };

  if (!isLoaded) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-50 text-gray-500 text-sm">
        <RotateCw className="w-5 h-5 animate-spin mr-2 text-blue-600" />
        Loading Google Maps…
      </div>
    );
  }


  return (
    <div
      ref={mapContainerRef}
      className={`w-full flex flex-col bg-white overflow-hidden select-none relative ${
        isFullscreen ? 'fixed inset-0 z-[9999]' : ''
      }`}
      style={{ height }}
    >
      {/* 1. TOP HORIZONTAL TOOLBAR */}
      <div className="bg-white border-b border-gray-200 px-4 py-2.5 flex items-center justify-between gap-3 text-xs shrink-0 flex-wrap z-10 shadow-sm">
        <div className="flex items-center gap-2.5 flex-wrap">
          {/* GPS Points Badge */}
          <div
            className={`flex items-center gap-1.5 px-3 py-1.5 text-white rounded-lg font-semibold text-xs shadow-sm transition-colors ${
              viewMode === 'detailed' ? 'bg-indigo-600' : 'bg-blue-600'
            }`}
          >
            <Navigation className="w-3.5 h-3.5" />
            <span>
              {routePath.length} GPS Points ({viewMode === 'detailed' ? 'Full 5s' : '30s summary'})
            </span>
          </div>

          {/* View Full Tracking (5s) vs Switch to Summary (30s) Toggle Button */}
          <button
            onClick={() => setViewMode(prev => (prev === 'detailed' ? 'summary' : 'detailed'))}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border font-semibold text-xs shadow-sm transition active:scale-95 cursor-pointer ${
              viewMode === 'detailed'
                ? 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
            title={viewMode === 'detailed' ? 'Switch to 30-second summary' : 'Show all raw 5-second GPS points'}
          >
            <MapPin className={`w-3.5 h-3.5 ${viewMode === 'detailed' ? 'text-amber-600' : 'text-blue-600'}`} />
            <span>{viewMode === 'detailed' ? 'Switch to Summary (30s)' : 'View Full Tracking (5s)'}</span>
          </button>

          {/* Time range pill */}
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-lg border border-gray-200 text-gray-700 font-medium text-xs">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            <span>
              {routeStats.startTime} - {routeStats.endTime}
            </span>
          </div>

          {/* Duration */}
          <div className="h-4 w-px bg-gray-200 mx-1 hidden sm:block" />
          <div className="text-xs text-gray-500 hidden sm:flex items-center gap-1">
            Duration <span className="font-bold text-gray-800">{routeStats.duration}</span>
          </div>

          {/* Distance */}
          <div className="h-4 w-px bg-gray-200 mx-1 hidden sm:block" />
          <div className="text-xs text-gray-500 hidden sm:flex items-center gap-1">
            <RouteIcon className="w-3.5 h-3.5 text-gray-400" />
            Distance <span className="font-bold text-gray-800">{routeStats.distance} km</span>
          </div>
        </div>

        {/* Fullscreen Button */}
        <button
          onClick={toggleFullscreen}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-gray-50 text-gray-700 rounded-lg border border-gray-200 font-semibold text-xs shadow-sm transition active:scale-95 cursor-pointer"
        >
          {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          <span>{isFullscreen ? 'Exit Full Screen' : 'Full Screen'}</span>
        </button>
      </div>

      {/* 2. MIDDLE SPLIT: MAP (LEFT) + SIDEBAR (RIGHT) */}
      <div className="flex-1 min-h-0 flex relative overflow-hidden">
        {/* Left: Map Area */}
        <div className="flex-1 h-full relative">
          {/* Floating Top-Left Card: First Point & Center Point */}
          {firstPoint && (
            <div className="absolute top-4 left-4 z-[5] bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-100 p-3 text-xs min-w-[210px] pointer-events-auto">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0" />
                <span className="font-semibold text-gray-900">First Point</span>
              </div>
              <div className="pl-4.5 text-gray-600 font-mono text-[11px] mb-2.5">
                {firstPoint.lat.toFixed(4)}, {firstPoint.lng.toFixed(4)}
              </div>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2.5 h-2.5 rounded-full bg-purple-600 shrink-0" />
                <span className="font-semibold text-gray-900">Center Point</span>
              </div>
              <div className="pl-4.5 text-gray-600 font-mono text-[11px]">
                {displayCenter.lat.toFixed(4)}, {displayCenter.lng.toFixed(4)} | Zoom: {displayZoom}
              </div>
            </div>
          )}

          {/* Floating Bottom-Left Card: Map Legend */}
          <div className="absolute bottom-5 left-5 z-[5] bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-100 p-4 min-w-[200px] pointer-events-auto">
            <div className="flex items-center gap-2 font-bold text-gray-900 text-xs mb-3">
              <span className="w-2 h-2 rounded-full bg-blue-600" />
              Map Legend
            </div>
            <div className="space-y-2 text-xs">
              {/* Pickup */}
              <div className="flex items-center gap-3">
                <img src={makePinSvg('blue')} alt="Pickup" className="w-5 h-7 object-contain shrink-0" />
                <div>
                  <div className="font-semibold text-gray-800 text-[11px] leading-tight">Pickup Point</div>
                  <div className="text-gray-500 text-[10px] leading-tight">Patient location</div>
                </div>
              </div>
              {/* Dropoff */}
              <div className="flex items-center gap-3">
                <img src={makePinSvg('red')} alt="Dropoff" className="w-5 h-7 object-contain shrink-0" />
                <div>
                  <div className="font-semibold text-gray-800 text-[11px] leading-tight">Dropoff Point</div>
                  <div className="text-gray-500 text-[10px] leading-tight">Hospital / Destination</div>
                </div>
              </div>
              {/* Ambulance (commented out as requested) */}
              {/*
              <div className="flex items-center gap-3">
                <img src={ambulanceIcon} alt="Ambulance" className="w-6 h-6 object-contain shrink-0" />
                <div>
                  <div className="font-semibold text-gray-800 text-[11px] leading-tight">Ambulance</div>
                  <div className="text-gray-500 text-[10px] leading-tight">Live vehicle location</div>
                </div>
              </div>
              */}
              {/* Actual Driver Route (Blue) */}
              <div className="flex items-center gap-3">
                <div className="w-6 h-1 bg-blue-600 rounded shrink-0" />
                <div>
                  <div className="font-semibold text-gray-800 text-[11px] leading-tight">Actual Travelled Route</div>
                  <div className="text-gray-500 text-[10px] leading-tight">Tracked path driven by ambulance</div>
                </div>
              </div>
              {/* Planned Route (Red) */}
              <div className="flex items-center gap-3">
                <div className="w-6 flex items-center justify-center shrink-0">
                  <svg width="24" height="8" viewBox="0 0 24 8">
                    <line x1="0" y1="4" x2="18" y2="4" stroke="#EF4444" strokeWidth="2.5" />
                    <polygon points="17,1 23,4 17,7" fill="#EF4444" />
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-gray-800 text-[11px] leading-tight">Planned Route (Original)</div>
                  <div className="text-gray-500 text-[10px] leading-tight">Scheduled Pickup → Dropoff</div>
                </div>
              </div>
              {/* GPS Point */}
              <div className="flex items-center gap-3">
                <div className="w-6 flex items-center justify-center shrink-0">
                  <svg width="14" height="14" viewBox="0 0 14 14">
                    <circle cx="7" cy="7" r="6" fill="#1A73E8" stroke="#FFFFFF" strokeWidth="1.5" />
                    <circle cx="7" cy="7" r="2.5" fill="#FFFFFF" />
                    <circle cx="7" cy="7" r="1.2" fill="#1A73E8" />
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-gray-800 text-[11px] leading-tight">GPS Point</div>
                  <div className="text-gray-500 text-[10px] leading-tight">Position over time</div>
                </div>
              </div>
            </div>
          </div>

          {/* Google Map Container */}
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            onLoad={onLoad}
            onUnmount={onUnmount}
            onDragStart={() => {
              userInteractedRef.current = true;
            }}
            onZoomChanged={() => {
              if (!isProgrammaticMoveRef.current) {
                userInteractedRef.current = true;
              }
            }}
            options={{
              zoomControl: showControls,
              mapTypeControl: false,
              scaleControl: true,
              streetViewControl: false,
              rotateControl: false,
              fullscreenControl: false,
              disableDefaultUI: !showControls,
              styles: uberMapStyle,
            }}
          >
            {/* 1. DIRECTIONS ROUTE (Planned Pickup → Dropoff along real road network) in RED */}
            {directionRoutePath.length >= 2 && window.google?.maps && (
              <>
                {/* Glow/Shadow line */}
                <Polyline
                  key={`planned-glow-${directionRoutePath.length}`}
                  path={directionRoutePath.map(p => ({ lat: p.lat, lng: p.lng }))}
                  options={{
                    strokeColor: '#DC2626',
                    strokeOpacity: 0.25,
                    strokeWeight: 8,
                    geodesic: true,
                    zIndex: 994,
                  }}
                />
                {/* Planned Directions Route Line (RED) */}
                <Polyline
                  key={`planned-main-${directionRoutePath.length}`}
                  path={directionRoutePath.map(p => ({ lat: p.lat, lng: p.lng }))}
                  options={{
                    strokeColor: '#EF4444',
                    strokeOpacity: 0.9,
                    strokeWeight: 4.5,
                    geodesic: true,
                    zIndex: 995,
                    icons: [
                      {
                        icon: {
                          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                          fillColor: '#EF4444',
                          fillOpacity: 1,
                          strokeColor: '#FFFFFF',
                          strokeWeight: 1.5,
                          scale: 2.8,
                        },
                        offset: '25px',
                        repeat: '70px',
                      },
                    ],
                  }}
                />
              </>
            )}

            {/* 2. DRIVER TRACKED ROUTE (Actual GPS path driven by driver) */}
            {actualDrivenRoutePath.length >= 2 && window.google?.maps && (
              <>
                {/* Glow/Shadow line */}
                <Polyline
                  key={`actual-route-glow-${actualDrivenRoutePath.length}`}
                  path={actualDrivenRoutePath}
                  options={{
                    strokeColor: '#1E40AF',
                    strokeOpacity: 0.25,
                    strokeWeight: 10,
                    geodesic: true,
                    zIndex: 996,
                  }}
                />
                {/* Main Blue Route with Forward Direction Arrows */}
                <Polyline
                  key={`actual-route-main-${actualDrivenRoutePath.length}`}
                  path={actualDrivenRoutePath}
                  options={{
                    strokeColor: '#1A73E8',
                    strokeOpacity: 1,
                    strokeWeight: 5,
                    geodesic: true,
                    zIndex: 998,
                    icons: [
                      {
                        icon: {
                          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                          fillColor: '#1A73E8',
                          fillOpacity: 1,
                          strokeColor: '#FFFFFF',
                          strokeWeight: 1.5,
                          scale: 3,
                        },
                        offset: '25px',
                        repeat: '65px',
                      },
                    ],
                  }}
                />
              </>
            )}
          </GoogleMap>
        </div>

        {/* Right: Sidebar Panel with Tabs */}
        <div className="w-72 md:w-80 h-full border-l border-gray-200 bg-white flex flex-col shrink-0 select-none">
          {/* Tab Switcher */}
          <div className="p-3 border-b border-gray-100 shrink-0 sticky top-0 bg-white z-10">
            <div className="flex bg-gray-100 p-1 rounded-xl gap-1">
              <button
                onClick={() => setActiveTab('timeline')}
                className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                  activeTab === 'timeline'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                <Clock className="w-3.5 h-3.5" />
                Timeline
              </button>
              <button
                onClick={() => setActiveTab('locations')}
                className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                  activeTab === 'locations'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                <List className="w-3.5 h-3.5" />
                Location List
              </button>
            </div>
          </div>

          {/* Scrollable Tab Content */}
          <div className="flex-1 overflow-y-auto px-4 py-3">
            {activeTab === 'timeline' ? (
              <div className="relative pl-2 py-1">
                {/* Vertical connecting line */}
                <div className="absolute left-[19px] top-4 bottom-4 w-0.5 bg-gray-200" />

                <div className="space-y-5">
                  {timelineMilestones.map((item, idx) => {
                    const isLast = idx === timelineMilestones.length - 1;
                    return (
                      <div
                        key={item.id || idx}
                        onClick={() =>
                          item.lat != null &&
                          item.lng != null &&
                          map?.panTo({ lat: item.lat, lng: item.lng })
                        }
                        className="relative flex items-start gap-3 group cursor-pointer"
                      >
                        {/* Milestone Node Icon */}
                        <div className="relative z-10 shrink-0 mt-0.5">
                          {isLast ? (
                            <div className="w-5 h-5 rounded-full bg-red-50 border-2 border-red-500 flex items-center justify-center shadow-sm">
                              <div className="w-2 h-2 rounded-full bg-red-600" />
                            </div>
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-blue-50 border-2 border-blue-600 flex items-center justify-center shadow-sm">
                              <div className="w-2 h-2 rounded-full bg-blue-600" />
                            </div>
                          )}
                        </div>

                        {/* Milestone Details */}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-bold text-gray-900 group-hover:text-blue-600 transition-colors leading-tight">
                            {item.title}
                          </div>
                          <div className="text-xs text-gray-500 font-medium mt-0.5 leading-tight">
                            {item.time}
                          </div>

                          {/* Address Badge for Pickup and Destination */}
                          {item.address && (
                            <div
                              className={`mt-1.5 p-2 rounded-lg border text-xs leading-snug flex items-start gap-1.5 shadow-xs ${
                                isLast
                                  ? 'bg-red-50/80 border-red-200/80 text-red-950'
                                  : 'bg-blue-50/80 border-blue-200/80 text-blue-950'
                              }`}
                            >
                              <MapPin
                                className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${
                                  isLast ? 'text-red-500' : 'text-blue-600'
                                }`}
                              />
                              <div className="flex-1 min-w-0">
                                <span className="font-semibold block text-[10px] uppercase tracking-wider text-gray-500">
                                  {isLast ? 'Destination' : 'Pickup Address'}:
                                </span>
                                <span className="font-medium text-xs text-gray-800 break-words">
                                  {item.address}
                                </span>
                              </div>
                            </div>
                          )}

                          {item.lat != null && item.lng != null && (
                            <div className="text-[11px] text-gray-400 font-mono mt-1 leading-tight">
                              {item.lat.toFixed(4)}, {item.lng.toFixed(4)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* Location List View */
              <div className="space-y-2 py-1">
                {routePath.length === 0 ? (
                  <div className="text-center py-8 text-xs text-gray-400">
                    No GPS location points recorded for this trip.
                  </div>
                ) : (
                  routePath.map((pt, idx) => (
                    <div
                      key={`loc-${idx}`}
                      onClick={() => map?.panTo({ lat: pt.lat, lng: pt.lng })}
                      className="p-2.5 rounded-xl border border-gray-100 hover:border-blue-200 hover:bg-blue-50/50 transition-all cursor-pointer flex items-center justify-between"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                            #{idx + 1}
                          </span>
                          <span className="text-xs font-semibold text-gray-800">
                            {pt.timestamp
                              ? new Date(pt.timestamp).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true,
                                })
                              : `Point ${idx + 1}`}
                          </span>
                        </div>
                        <div className="text-[11px] text-gray-500 font-mono mt-1">
                          {pt.lat.toFixed(4)}, {pt.lng.toFixed(4)}
                        </div>
                      </div>
                      {msToKmh(pt.speed) != null && (
                        <span className="text-[11px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                          {formatSpeedKmh(pt.speed)}
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 3. BOTTOM FOOTER BAR */}
      <div className="bg-white border-t border-gray-200 px-6 py-3 flex items-center justify-between gap-4 flex-wrap shrink-0 z-10 shadow-sm">
        {/* Left: Route Information */}
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
            <Info className="w-4 h-4" />
          </div>
          <div>
            <div className="text-xs font-bold text-gray-900 leading-tight">Route Information</div>
            <div className="text-xs text-gray-500 leading-tight">
              This map shows the actual GPS tracking data and driven route for this trip.
            </div>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2.5">
          <button
            onClick={handleRefresh}
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 shadow-sm transition active:scale-95 cursor-pointer"
          >
            <RotateCw className={`w-3.5 h-3.5 text-gray-600 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={toggleFullscreen}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition active:scale-95 cursor-pointer"
          >
            <Maximize2 className="w-3.5 h-3.5" />
            <span>Open in Full Screen</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoutePlaybackMap;
